// import { debounce } from '@mui/material';
import { search } from 'app/blocks/middleware/search';

export const PAGE_SIZE = 10;
export const DEFAULT_OFFSET = 0;

export type StaticSearchErrorKind = 'connection' | 'generic';

export type StaticSearchItemsCallback = (
    results: readonly Search.Static[] | undefined,
    errorKind?: StaticSearchErrorKind,
) => void;

export function classifyStaticSearchError(error: unknown): StaticSearchErrorKind {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'connection';
    }
    const err = error as { code?: string; message?: string };
    if (err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK') {
        return 'connection';
    }
    if (typeof err?.message === 'string' && err.message.toLowerCase().includes('network')) {
        return 'connection';
    }
    return 'generic';
}

export const STATIC_SEARCH_MAX_QUERY_LENGTH = 200;

// @ts-ignore -- unicode property escapes require ES2018+ runtime; suppressed since tsconfig target predates it but browserslist targets modern browsers
const CONTROL_CHARS_PATTERN = /\p{Cc}/gu;
// @ts-ignore -- unicode property escapes require ES2018+ runtime; suppressed since tsconfig target predates it but browserslist targets modern browsers
const DISALLOWED_QUERY_CHARS_PATTERN = /[^\p{L}\p{M}\p{N}\s.,'\u2018\u2019@#%&/\-–—]/gu;

export function sanitise(query: string): string {
    if (!query) {
        return '';
    }

    return query
        .replace(CONTROL_CHARS_PATTERN, '')
        .replace(DISALLOWED_QUERY_CHARS_PATTERN, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, STATIC_SEARCH_MAX_QUERY_LENGTH);
}

const waiter = (ms: number, signal: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        signal.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        });
    });

interface FetchItemsFunc {
    (params: { query: string; timeout?: number; size?: number; offset?: number; onStart?: () => void }): Promise<{
        staticItems?: readonly Search.Static[];
        errorKind?: StaticSearchErrorKind;
    }>;
}

let debounceController: AbortController | null = null;

export const fetchItems: FetchItemsFunc = async params => {
    const { offset = DEFAULT_OFFSET, onStart, query, size = PAGE_SIZE, timeout = 0 } = params;

    if (timeout > 0) {
        debounceController?.abort();
        debounceController = new AbortController();
        try {
            await waiter(timeout, debounceController.signal);
        } catch {
            return {};
        }
    }

    onStart?.();
    try {
        const data = await search(query, { offset, size }, 'static');
        const staticItems = data.items.filter((item): item is Search.Static => item.type === 'static');
        return { staticItems };
    } catch (error) {
        return { errorKind: classifyStaticSearchError(error) };
    }
};
